import { useState } from "react";
import { Camera, ExternalLink, Music2, Play } from "lucide-react";

/**
 * « Actualité » (/docs) : news posts with embedded YouTube videos.
 *
 * Privacy (RGPD / CNIL): nothing is loaded from YouTube until the visitor clicks
 * Play. Until then we show a self-hosted poster (public/actualite/*), so no
 * request — not even a thumbnail — reaches Google on page load. On click, the
 * player comes from youtube-nocookie.com (privacy-enhanced mode).
 *
 * To add a post: add an entry at the top of POSTS. For a video, give its
 * YouTube id (the part after watch?v= or /shorts/), "video" (16:9) or "short"
 * (9:16), and a poster image placed in public/actualite/.
 */

interface Video {
  youtubeId: string;
  kind: "video" | "short";
  title: string;
  poster: string;
  duration: string; // "1:22"
}

interface Post {
  id: string;
  title: string;
  date: string; // ISO, displayed in French
  body: string[];
  videos?: Video[];
}

const POSTS: Post[] = [
  {
    id: "flowboard-en-video",
    title: "Flowboard en vidéo",
    date: "2026-10-02",
    body: [
      "Vos écrans affichent encore les horaires d'été ou le menu de la semaine dernière ? En 80 secondes, découvrez comment Flowboard met à jour toutes vos TV depuis votre navigateur : créer un écran, partir d'un modèle, ajouter des widgets en direct, programmer vos créneaux et associer une TV avec un simple code.",
      "La version courte, en 30 secondes, est aussi disponible en Short.",
    ],
    videos: [
      {
        youtubeId: "iYGLte3ftEc",
        kind: "video",
        title: "Flowboard : l'affichage dynamique simple pour tous vos écrans",
        poster: "/actualite/flowboard-lancement.jpg",
        duration: "1:22",
      },
      {
        youtubeId: "GjGlVpBDew4",
        kind: "short",
        title: "Vos écrans affichent encore des infos périmées ?",
        poster: "/actualite/flowboard-short-infos-perimees.jpg",
        duration: "0:30",
      },
    ],
  },
  {
    id: "lancement",
    title: "Lancement de Flowboard",
    date: "2025-05-17",
    body: [
      "Flowboard est maintenant disponible. Gérez vos écrans dynamiques depuis un tableau de bord centralisé, créez des playlists et déployez votre instance en quelques minutes.",
    ],
  },
];

const SOCIALS = [
  { label: "YouTube", handle: "@Canopesaas", href: "https://www.youtube.com/@Canopesaas", icon: Play },
  { label: "Instagram", handle: "canope_org", href: "https://www.instagram.com/canope_org/", icon: Camera },
  { label: "TikTok", handle: "@canope_org", href: "https://www.tiktok.com/@canope_org", icon: Music2 },
];

function formatDate(iso: string) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function YouTubeEmbed({ video }: { video: Video }) {
  const [playing, setPlaying] = useState(false);
  const isShort = video.kind === "short";
  const watchUrl = isShort
    ? `https://www.youtube.com/shorts/${video.youtubeId}`
    : `https://www.youtube.com/watch?v=${video.youtubeId}`;

  return (
    <figure className={isShort ? "w-full max-w-[280px]" : "w-full"}>
      <div
        className={`relative overflow-hidden rounded-xl bg-gray-900 shadow-sm ring-1 ring-black/5 ${
          isShort ? "aspect-[9/16]" : "aspect-video"
        }`}
      >
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Lire la vidéo : ${video.title}`}
          >
            <img src={video.poster} alt="" loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/25" />
            {/* Bottom-left so it never hides the poster's title. */}
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-xl bg-indigo-600 py-2 pl-3 pr-4 text-sm font-semibold text-white shadow-lg transition-transform group-hover:scale-105">
              <Play className="size-4 fill-current" />
              {isShort ? "Short" : "Lire"} · {video.duration}
            </span>
          </button>
        )}
      </div>
      <figcaption className="mt-2 text-xs leading-relaxed text-gray-500">
        <span className="font-medium text-gray-700">{video.title}</span>
        <br />
        {!playing && "La vidéo est hébergée sur YouTube : elle ne se charge que si vous cliquez sur Lecture. "}
        <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
          Voir sur YouTube <ExternalLink className="size-3" />
        </a>
      </figcaption>
    </figure>
  );
}

export function Actualite() {
  return (
    <div className="max-w-3xl">
      <h1 className="mb-2 text-2xl font-bold text-gray-900">Actualité</h1>
      <p className="mb-6 text-gray-500">Les nouveautés de Flowboard, en texte et en vidéo.</p>

      <div className="mb-10 flex flex-wrap gap-2">
        {SOCIALS.map(({ label, handle, href, icon: Icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
          >
            <Icon className="size-4" />
            {label}
            <span className="text-gray-400">{handle}</span>
          </a>
        ))}
      </div>

      <div className="space-y-12">
        {POSTS.map((post) => {
          const main = post.videos?.filter((v) => v.kind === "video") ?? [];
          const shorts = post.videos?.filter((v) => v.kind === "short") ?? [];
          return (
            <article key={post.id} id={`actualite-${post.id}`} className="border-b border-gray-100 pb-12 last:border-0">
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">{formatDate(post.date)}</p>
              <h2 className="mb-3 text-lg font-semibold text-gray-900">{post.title}</h2>
              <div className="space-y-3">
                {post.body.map((p) => (
                  <p key={p} className="leading-relaxed text-gray-600">{p}</p>
                ))}
              </div>
              {main.length > 0 && (
                <div className="mt-6 space-y-6">
                  {main.map((v) => (
                    <YouTubeEmbed key={v.youtubeId} video={v} />
                  ))}
                </div>
              )}
              {shorts.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-6">
                  {shorts.map((v) => (
                    <YouTubeEmbed key={v.youtubeId} video={v} />
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
